import React from 'react'
import {Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {spaceExtraExtraSmall, spaceMedium, spaceSmall} from '../../assets/styles/spacing'
import {
  rxrDarkGreyColor,
  rxrGreyColor,
  rxrMediumLightGreyColor,
  rxrWhiteColor,
  rxrGreenColor,
  rxrOrangeColor,
  rxrRedColor,
} from '../../assets/styles/color'
import {fontSizeLarge, fontSizeMedium, heavyFontWeight, lightFontWeight} from '../../assets/styles/typography'
import {borderRadiusMedium} from '../../assets/styles/border'
import ArcChart from './ArcChart'
import {getChangeStyle} from './utils'
import NPSScoreBreakdownTable from './NPSScoreBreakdownTable'
import NPSScoreBreakdownChart from './NPSScoreBreakdownChart'
import PropTypes from 'prop-types'
import {renderNotAvailableIfValIsNaN, renderFloatWithTwoDecimalsIfValIsNotNaN} from './residentSentimentUtilities'

const useStyles = makeStyles(theme => ({
  cardLarge: {
    width: '350px',
    height: '304px',
    borderRadius: '10px',
    border: `1px solid ${rxrMediumLightGreyColor}`,
    background: rxrWhiteColor,
    padding: spaceSmall,
  },
  // only the exit-survey page renders the wide variant, so it follows that page's card styling
  // (borderRadiusMedium + rxrGreyColor border); the compact variant sits beside the sentiment cards
  // on Resident Sentiment and keeps their 10px / darker border
  cardWide: {
    width: '100%',
    borderRadius: borderRadiusMedium,
    border: `1px solid ${rxrGreyColor}`,
    background: rxrWhiteColor,
    padding: spaceMedium,
  },
  cardColumns: {
    display: 'flex',
    alignItems: 'center',
    gap: spaceMedium,
    paddingTop: spaceMedium,

    '& > div': {
      flex: 1,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardTitle: {
    fontSize: fontSizeLarge,
    fontWeight: heavyFontWeight,
    alignSelf: 'flex-start',
  },
  arcChartContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: spaceSmall,
  },

  npsChangeText: {
    marginLeft: spaceMedium,
  },

  npsScoreChangeValue: {
    fontSize: fontSizeMedium,
    fontWeight: lightFontWeight,
  },

  npsScoreChangeDescription: {
    fontSize: fontSizeMedium,
    fontWeight: lightFontWeight,
    color: rxrDarkGreyColor,
    marginLeft: spaceExtraExtraSmall,
  },
}))

function NPSScoreCard({
  cardTitle,
  titleControl,
  compact,
  npsResponses,
  npsScore,
  npsScoreChange,
  npsScorePercentagePromoters,
  npsScorePercentagePromotersChangeFromLastMonth,
  npsScorePercentagePassives,
  npsScorePercentagePassivesChangeFromLastMonth,
  npsScorePercentageDetractors,
  npsScorePercentageDetractorsChangeFromLastMonth,
}) {
  const classes = useStyles({
    npsScorePercentagePromoters,
    npsScorePercentagePassives,
    npsScorePercentageDetractors,
  })

  // Determine the color and sign of the npsScoreChange
  let {color, sign} = getChangeStyle(npsScoreChange)

  const arcChartAndChangeText = (
    <div className={classes.arcChartContainer}>
      <ArcChart
        score={typeof npsScore === 'number' ? npsScore.toFixed(0) : null}
        width={60}
        arcColor={npsScore > 30 ? rxrGreenColor : npsScore < -30 ? rxrRedColor : rxrOrangeColor}
      />
      <div className={classes.npsChangeText}>
        <Typography className={classes.npsScoreChangeValue} style={{color: color}}>
          {sign}
          {renderFloatWithTwoDecimalsIfValIsNotNaN(npsScoreChange)}
          <span className={classes.npsScoreChangeDescription}>from last month</span>
        </Typography>
        <Typography className={classes.npsScoreChangeValue}>{renderNotAvailableIfValIsNaN(npsResponses)} responses</Typography>
      </div>
    </div>
  )

  const breakdownChartAndTable = (
    <>
      <NPSScoreBreakdownChart
        npsScorePercentagePromoters={npsScorePercentagePromoters}
        npsScorePercentagePassives={npsScorePercentagePassives}
        npsScorePercentageDetractors={npsScorePercentageDetractors}
      />
      <NPSScoreBreakdownTable
        npsScorePercentagePromoters={npsScorePercentagePromoters}
        npsScorePercentagePromotersChangeFromLastMonth={npsScorePercentagePromotersChangeFromLastMonth}
        npsScorePercentagePassives={npsScorePercentagePassives}
        npsScorePercentagePassivesChangeFromLastMonth={npsScorePercentagePassivesChangeFromLastMonth}
        npsScorePercentageDetractors={npsScorePercentageDetractors}
        npsScorePercentageDetractorsChangeFromLastMonth={npsScorePercentageDetractorsChangeFromLastMonth}
      />
    </>
  )

  const header = (
    <div className={classes.cardHeader}>
      <Typography className={classes.cardTitle}>{cardTitle}</Typography>
      {titleControl}
    </div>
  )

  if (compact) {
    return (
      <div className={classes.cardLarge}>
        {header}
        {arcChartAndChangeText}
        {breakdownChartAndTable}
      </div>
    )
  }

  return (
    <div className={classes.cardWide}>
      {header}
      <div className={classes.cardColumns}>
        <div>{arcChartAndChangeText}</div>
        <div>{breakdownChartAndTable}</div>
      </div>
    </div>
  )
}

NPSScoreCard.propTypes = {
  cardTitle: PropTypes.string.isRequired,
  titleControl: PropTypes.node,
  compact: PropTypes.bool,
  npsResponses: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
  npsScore: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
  npsScoreChange: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
  npsScorePercentagePromoters: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
  npsScorePercentagePromotersChangeFromLastMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
  npsScorePercentagePassives: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
  npsScorePercentagePassivesChangeFromLastMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
  npsScorePercentageDetractors: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
  npsScorePercentageDetractorsChangeFromLastMonth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['NaN'])]).isRequired,
}

export default NPSScoreCard
